<template>
    <SbsBaseButton
        v-bind="$attrs"
        class="sbs-button trs"
        :class="buttonClasses"
        :to="to"
        :disabled="isDisabled">
        <!-- @slot Слот для лоадера. Доступна пропса `loading` -->
        <slot
            name="loader"
            v-bind="{ loading }">
            <SbsDotLoader
                v-if="loading"
                class="sbs-button__loader" />
        </slot>

        <div class="sbs-button__content">
            <!-- @slot Слот перед контентом -->
            <slot name="prepend"></slot>

            <!-- @slot Слот по-умолчанию -->
            <slot>
                <span
                    v-if="text"
                    class="sbs-button__text">
                    {{ text }}
                </span>
            </slot>

            <!-- Элемент для отображения уведомления -->
            <div
                v-if="withNotify"
                class="sbs-button__notify">
            </div>

            <!-- @slot Слот после контента -->
            <slot name="append"></slot>
        </div>

        <!-- @slot Слот для счётчика. Доступна пропса `counter` -->
        <slot
            name="counter"
            v-bind="{ counter }">
            <SbsBaseCounter
                v-if="counter"
                class="sbs-button__counter"
                v-bind="buttonCounterData" />
        </slot>
    </SbsBaseButton>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import { AllColors } from '../../utils/colors.js';
import {
    isArrayContainsValue,
} from '../../utils/validators.js';
import {
    AllSizes,
    BUTTON_SIZES,
} from '../../utils/sizes.js';

/**
 * Компонент кнопка
 */
export default {
    name: 'SbsButton',
    mixins: [themesMixin],
    computed: {
        isDisabled() {
            return this.disabled || this.loading;
        },
        buttonClasses() {
            return [
                ...this.themeClasses,
                this.size,
                {
                    'disabled': this.disabled,
                    'loading': this.loading,
                    'static': this.static,
                    'bordered': this.bordered,
                    'full-width': this.fullWidth,
                },
            ];
        },
        buttonCounterData() {
            return {
                value: this.counter,
                backgroundTheme: AllColors.GRADIENT_PINK,
                bordered: true,
            };
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Определяет наличие бордера у кнопки
         */
        bordered: {
            type: Boolean,
            default: true,
        },
        /**
         * Тема (цветовые решения) для цвета бордера
         */
        // eslint-disable-next-line vue/no-unused-properties
        borderTheme: {
            type: String,
            default: AllColors.BLACK,
        },
        /**
         * Тема (цветовые решения) для цвета текста
         */
        // eslint-disable-next-line vue/no-unused-properties
        colorTheme: {
            type: String,
            default: AllColors.BLACK,
        },
        /**
         * Значение счётчика
         */
        counter: {
            type: [Number, String],
            default: null,
        },
        /**
         * Отключает взаимодействие с компонентом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Растягивает кнопку на всю ширину родительского блока
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
        /**
         * Состояние загрузки
         */
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Размер кнопки
         */
        size: {
            type: String,
            default: AllSizes.MEDIUM,
            validator: isArrayContainsValue(BUTTON_SIZES),
        },
        /**
         * Определяет будет ли визуально подниматься кнопка при наведении
         */
        static: {
            type: Boolean,
            default: false,
        },
        /**
         * Текст кнопки
         */
        text: {
            type: String,
            default: '',
        },
        /**
         * Роут для `<RouterLink>` для перехода на другую страницу без перезагрузки.
         * Может быть строкой, либо объектом.
         * Имеет более высокий приоритет перед `href`
         */
        to: {
            type: [Object, String],
            default: null,
        },
        /**
         * Иконка для показа иконки уведомления на кнопки
         */
        withNotify: {
            type: Boolean,
            default: false,
        },
    },
};
</script>

<style lang="scss">
.sbs-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    max-width: 100%;
    padding: 7px 25px;
    text-decoration: none;
    border-radius: $rounded-pill;

    @include has-hover {
        &:not(.disabled):not(.loading):not(.static):hover {
            transform: translateY(-4px);
        }
    }

    &:not(.disabled):not(.loading):not(.static):focus {
        transform: translateY(-4px);
    }

    &.x-small {
        padding: 7px 15px;

        @include typo-additional-book;
    }

    &.small {
        padding: 7px 15px;

        @include typo-secondary-book;
    }

    &.large {
        padding: 12px 40px;
    }

    &.x-large {
        padding: 8px 40px;

        @include typo-primary-demi;
    }

    &.full-width {
        width: 100%;
    }

    &.loading {
        & > * {
            opacity: 0;
        }
    }

    &:not(.loading).disabled {
        opacity: 0.5;
    }

    &__content {
        display: inherit;
        flex-direction: inherit;
        flex-grow: 1;
        align-items: inherit;
        justify-content: inherit;
        max-width: 100%;
        max-height: 100%;
    }

    &__text {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__notify {
        position: absolute;
        top: 0;
        right: -2px;
        width: 13px;
        height: 13px;
        background: $gradient-crimson;
        border: 2px solid $white;
        border-radius: 50px;
    }

    &.small &__notify {
        width: 11px;
        height: 11px;
    }

    &__counter {
        position: absolute;
        top: -5px;
        right: -5px;

        .sbs-button.loading & {
            opacity: 1;
        }
    }

    &__loader {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        .sbs-button.loading & {
            opacity: 1;
        }
    }
}
</style>
